export type GimcheonSchoolScheduleKind = "exam" | "event" | "holiday" | "notice";

export type GimcheonSchoolScheduleSource = "official" | "provided";

export type GimcheonSchoolScheduleItem = {
    date: string;
    endDate?: string;
    dateLabel: string;
    title: string;
    kind: GimcheonSchoolScheduleKind;
    details: string;
    source: GimcheonSchoolScheduleSource;
};

export type GimcheonSchoolNewsItem = {
    date: string;
    dateLabel: string;
    title: string;
    summary: string;
    sourceUrl: string;
};

export type GimcheonSchoolProfileFact = {
    label: string;
    value: string;
    note?: string;
};

export type GimcheonSchoolSymbol = {
    label: string;
    value: string;
    meaning: string;
};

export type GimcheonSchoolListBlock = {
    title: string;
    items: string[];
};

export type GimcheonSchoolMealItem = {
    date: string;
    kcal: string;
    menu: string[];
};

export type GimcheonSchoolDemoData = {
    updatedAt: string;
    nameKo: string;
    nameEn: string;
    slogan: string;
    logoUrl: string;
    address: string;
    phone: string;
    adminPhone: string;
    dutyPhone: string;
    fax: string;
    principal: string;
    vicePrincipal: string;
    motto: string;
    founder: string;
    foundingIdea: string;
    identitySummary: string;
    educationGoal: string;
    establishment: string;
    gender: string;
    foundedDate: string;
    openedDate: string;
    officialUrl: string;
    heroImageUrl: string;
    dataNotice: string;
    privacyBoundary: string;
    quickQuestions: string[];
    profileFacts: GimcheonSchoolProfileFact[];
    symbols: GimcheonSchoolSymbol[];
    currentStatus: {
        basis: string;
        students: string;
        classes: string;
        staff: string;
        graduationDestinations: string[];
        note: string;
    };
    facilities: string[];
    schedule: GimcheonSchoolScheduleItem[];
    news: GimcheonSchoolNewsItem[];
    notices: GimcheonSchoolNewsItem[];
    familyLetters: GimcheonSchoolNewsItem[];
    healthRoomItems: string[];
    mealInfo: {
        basis: string;
        allergyNumbers: string[];
        menus: GimcheonSchoolMealItem[];
    };
    menuStructure: GimcheonSchoolListBlock[];
    chatGuidelines: string[];
};

export const GIMCHEON_SCHOOL_DEMO: GimcheonSchoolDemoData = {
    updatedAt: "2026-04-28",
    nameKo: "김천중학교",
    nameEn: "Gimcheon Middle School",
    slogan: "눈처럼 하얗게 솔처럼 푸르게",
    logoUrl: "/images/gimcheon-middle-logo.webp",
    address: "경상북도 김천시 송설로 90",
    phone: "054-433-0612",
    adminPhone: "054-433-0611",
    dutyPhone: "054-429-9710",
    fax: "054-436-3100",
    principal: "장영규",
    vicePrincipal: "김호균",
    motto: "깨끗하게 부지런하게",
    founder: "최송설당",
    foundingIdea: "영위사학 함양민족정신",
    identitySummary:
        "김천중학교는 1931년 최송설당 선생의 교육 정신을 바탕으로 개교한 송설학원의 사립 남자 중학교입니다.",
    educationGoal: "21세기 디지털 인공지능 시대를 선도할 새로운 생각과 참된 마음을 지닌 으뜸 송설인 육성",
    establishment: "사립 단설 중학교",
    gender: "남학교",
    foundedDate: "1931년 3월 17일 설립 인가",
    openedDate: "1931년 5월 9일 개교",
    officialUrl: "https://school.gyo6.net/gimcheonms",
    heroImageUrl: "https://school.gyo6.net/upload/ad/schl/gimcheonms/widg/img_2bc0vd129%3D0cv77%3D4fvc0%3Daev98%3D598dvf7e0vd926_v4369.png",
    dataNotice: "학교 홈페이지 공개 정보와 제공된 2026학년도 학사일정 기준 안내입니다.",
    privacyBoundary: "학생 개인 정보, 성적, 상담 내용, 내부 업무, 비공개 일정은 안내하지 않습니다.",
    quickQuestions: [
        "이번 달 학부모가 챙길 일정은?",
        "오늘 점심 뭐야?",
        "2학년 수학여행 언제야?",
        "김천중학교는 어떤 학교야?",
        "급식 알레르기 번호 알려줘"
    ],
    profileFacts: [
        {
            label: "개교",
            value: "1931.05.09",
            note: "김천고등보통학교 개교"
        },
        {
            label: "교훈",
            value: "깨끗하게 부지런하게"
        },
        {
            label: "2026 학생",
            value: "201명",
            note: "학교 홈페이지 2026년 3월 기준"
        },
        {
            label: "AI 교육",
            value: "AI 선도학교",
            note: "2023년 인공지능 선도학교, 2026년 AI 선도학교"
        }
    ],
    symbols: [
        {
            label: "교목",
            value: "소나무",
            meaning: "늘 푸른 기상과 의지력, 고고한 품성, 이타심, 끝없는 발전을 상징합니다."
        },
        {
            label: "교화",
            value: "진달래",
            meaning: "선구자적 자질, 조화의 아름다움, 겸양의 미덕, 한국인의 전통 정서를 상징합니다."
        }
    ],
    currentStatus: {
        basis: "2026년 3월 학교 홈페이지 기준",
        students: "1학년 51명, 2학년 51명, 3학년 99명, 총 201명",
        classes: "총 8학급, 특수학급 1학급 포함",
        staff: "교직원 현황 총 31명",
        graduationDestinations: [
            "김천고 5명",
            "김천생명과학고 6명",
            "구미전자고 2명",
            "성의고 16명",
            "김천중앙고 6명",
            "한일고 14명",
            "경북체고 1명"
        ],
        note: "학교알리미 계열 공개 정보 일부는 학생 수 199명, 교원 수 19명으로 표시될 수 있어 기준을 함께 말해야 합니다."
    },
    facilities: [
        "교장실",
        "행정실",
        "보건실",
        "교무센터",
        "진로활동실",
        "학생회의실",
        "도서관",
        "컴퓨터실",
        "서예실",
        "체육실",
        "영어실",
        "수학실",
        "미술실",
        "음악실",
        "연주연습실",
        "과학실 2실",
        "Wee클래스/사랑반",
        "일반교실 9실",
        "창업동아리실",
        "운동장",
        "농구장"
    ],
    schedule: [
        {
            date: "2026-04-27",
            endDate: "2026-04-29",
            dateLabel: "4.27(월)-4.29(수)",
            title: "1차 고사",
            kind: "exam",
            details: "학교 홈페이지 공개 일정 기준으로 2·3학년 1차 고사가 예정되어 있습니다.",
            source: "official"
        },
        {
            date: "2026-05-01",
            dateLabel: "5.1(금)",
            title: "임시공휴일",
            kind: "holiday",
            details: "제공된 2026학년도 학사일정 기준 임시공휴일입니다.",
            source: "provided"
        },
        {
            date: "2026-05-04",
            dateLabel: "5.4(월)",
            title: "재량휴업일",
            kind: "holiday",
            details: "제공된 2026학년도 학사일정 기준 재량휴업일입니다.",
            source: "provided"
        },
        {
            date: "2026-05-05",
            dateLabel: "5.5(화)",
            title: "어린이날",
            kind: "holiday",
            details: "공휴일로 수업이 없습니다.",
            source: "official"
        },
        {
            date: "2026-05-07",
            dateLabel: "5.7(목)",
            title: "체육대회",
            kind: "event",
            details: "제공된 2026학년도 학사일정 기준 체육대회가 예정되어 있습니다.",
            source: "provided"
        },
        {
            date: "2026-05-09",
            dateLabel: "5.9(토)",
            title: "개교기념일",
            kind: "event",
            details: "김천중학교 개교기념일입니다.",
            source: "official"
        },
        {
            date: "2026-05-11",
            endDate: "2026-05-14",
            dateLabel: "5.11(월)-5.14(목)",
            title: "2학년 수학여행",
            kind: "event",
            details: "제공된 2026학년도 학사일정 기준입니다. 장소, 준비물, 세부 일정은 가정통신문이나 담임선생님 안내를 확인해야 합니다.",
            source: "provided"
        },
        {
            date: "2026-05-18",
            dateLabel: "5.18(월)",
            title: "학부모 상담주간",
            kind: "notice",
            details: "제공된 2026학년도 학사일정 기준 학부모 상담주간이 시작됩니다.",
            source: "provided"
        },
        {
            date: "2026-05-24",
            dateLabel: "5.24(일)",
            title: "부처님오신날",
            kind: "holiday",
            details: "공휴일입니다.",
            source: "official"
        },
        {
            date: "2026-05-25",
            dateLabel: "5.25(월)",
            title: "대체공휴일",
            kind: "holiday",
            details: "부처님오신날 대체공휴일입니다.",
            source: "official"
        },
        {
            date: "2026-06-03",
            dateLabel: "6.3(수)",
            title: "지방선거일",
            kind: "holiday",
            details: "제공된 2026학년도 학사일정 기준 지방선거일입니다.",
            source: "provided"
        },
        {
            date: "2026-06-06",
            dateLabel: "6.6(토)",
            title: "현충일",
            kind: "holiday",
            details: "공휴일입니다.",
            source: "official"
        },
        {
            date: "2026-07-06",
            endDate: "2026-07-08",
            dateLabel: "7.6(월)-7.8(수)",
            title: "2차 고사",
            kind: "exam",
            details: "제공된 2026학년도 학사일정 기준 2·3학년 2차 고사입니다.",
            source: "provided"
        },
        {
            date: "2026-07-08",
            endDate: "2026-07-10",
            dateLabel: "7.8(수)-7.10(금)",
            title: "야영",
            kind: "event",
            details: "제공된 2026학년도 학사일정 기준입니다. 장소와 세부 안내는 학교 공지나 가정통신문을 확인해야 합니다.",
            source: "provided"
        },
        {
            date: "2026-07-21",
            dateLabel: "7.21(화)",
            title: "여름방학식",
            kind: "event",
            details: "제공된 2026학년도 학사일정 기준 여름방학식입니다.",
            source: "provided"
        },
        {
            date: "2026-08-18",
            dateLabel: "8.18(화)",
            title: "여름방학 개학식",
            kind: "event",
            details: "제공된 2026학년도 학사일정 기준 여름방학 개학식입니다.",
            source: "provided"
        },
        {
            date: "2026-09-01",
            endDate: "2026-09-03",
            dateLabel: "9.1(화)-9.3(목)",
            title: "영어듣기평가",
            kind: "exam",
            details: "1학년 9월 1일, 2학년 9월 2일, 3학년 9월 3일 영어듣기평가가 예정되어 있습니다.",
            source: "provided"
        },
        {
            date: "2026-09-30",
            endDate: "2026-10-02",
            dateLabel: "9.30(수)-10.2(금)",
            title: "2학기 1차 고사",
            kind: "exam",
            details: "제공된 2026학년도 학사일정 기준 1·2·3학년 1차 고사입니다.",
            source: "provided"
        },
        {
            date: "2026-10-22",
            dateLabel: "10.22(목)",
            title: "한아람 축제",
            kind: "event",
            details: "제공된 2026학년도 학사일정 기준 한아람 축제가 예정되어 있습니다.",
            source: "provided"
        },
        {
            date: "2026-10-23",
            dateLabel: "10.23(금)",
            title: "송설왕선발대회",
            kind: "event",
            details: "제공된 2026학년도 학사일정 기준 송설왕선발대회가 예정되어 있습니다.",
            source: "provided"
        },
        {
            date: "2026-10-24",
            dateLabel: "10.24(토)",
            title: "문학기행",
            kind: "event",
            details: "제공된 2026학년도 학사일정 기준 문학기행이 예정되어 있습니다.",
            source: "provided"
        },
        {
            date: "2026-11-11",
            endDate: "2026-11-13",
            dateLabel: "11.11(수)-11.13(금)",
            title: "3학년 2차 고사",
            kind: "exam",
            details: "제공된 2026학년도 학사일정 기준 3학년 2차 고사입니다.",
            source: "provided"
        },
        {
            date: "2026-12-09",
            endDate: "2026-12-11",
            dateLabel: "12.9(수)-12.11(금)",
            title: "1·2학년 2차 고사",
            kind: "exam",
            details: "제공된 2026학년도 학사일정 기준 1·2학년 2차 고사입니다.",
            source: "provided"
        },
        {
            date: "2026-12-12",
            dateLabel: "12.12(토)",
            title: "내한 마라톤",
            kind: "event",
            details: "학교 연혁에도 공개된 전통 행사입니다.",
            source: "provided"
        },
        {
            date: "2026-12-24",
            dateLabel: "12.24(목)",
            title: "청소년 팝스오케스트라",
            kind: "event",
            details: "제공된 2026학년도 학사일정 기준 청소년 팝스오케스트라 행사가 예정되어 있습니다.",
            source: "provided"
        },
        {
            date: "2027-01-07",
            dateLabel: "2027.1.7(목)",
            title: "졸업식·종업식",
            kind: "event",
            details: "제공된 2026학년도 학사일정 기준 졸업식과 종업식이 예정되어 있습니다.",
            source: "provided"
        }
    ],
    news: [
        {
            date: "2026-04-28",
            dateLabel: "2026.04.28",
            title: "2026년 사이버폭력예방 교육 실시",
            summary: "4월 23일 7교시에 세심관에서 올바른 온라인 소통과 디지털 시민의식 교육을 진행했습니다.",
            sourceUrl: "https://school.gyo6.net/gimcheonms/na/ntt/selectNttInfo.do?mi=126240&nttSn=16833855"
        },
        {
            date: "2026-04-17",
            dateLabel: "2026.04.17",
            title: "2026학년도 1학년 대상 성·피임교육 실시",
            summary: "1학년 학생을 대상으로 보건 교육 프로그램을 실시했습니다.",
            sourceUrl: "https://school.gyo6.net/gimcheonms/na/ntt/selectNttInfo.do?mi=126240&nttSn=16821421"
        },
        {
            date: "2026-04-15",
            dateLabel: "2026.04.15",
            title: "2026학년도 학부모 간담회 개최",
            summary: "교장실에서 학부모회 위촉장 수여와 학교 운영 의견 청취가 진행됐습니다.",
            sourceUrl: "https://school.gyo6.net/gimcheonms/na/ntt/selectNttInfo.do?mi=126240&nttSn=16817585"
        },
        {
            date: "2026-04-15",
            dateLabel: "2026.04.15",
            title: "감사운동 실천 시범학교 업무협약 체결",
            summary: "상록수나눔재단과 감사 실천 프로그램 운영을 위한 업무협약을 맺었습니다.",
            sourceUrl: "https://school.gyo6.net/gimcheonms/na/ntt/selectNttInfo.do?mi=126240&nttSn=16817436"
        }
    ],
    notices: [
        {
            date: "2026-04-17",
            dateLabel: "2026.04.17",
            title: "2026학년도 학생생활규정 제·개정안 시행 안내",
            summary: "학생생활규정 제·개정안은 2026년 5월 1일 시행 예정으로 공지되었습니다.",
            sourceUrl: "https://school.gyo6.net/gimcheonms/na/ntt/selectNttList.do?bbsId=68815&mi=126238"
        },
        {
            date: "2026-03-26",
            dateLabel: "2026.03.26",
            title: "제14기 학교운영위원회 지역위원 당선자 공고",
            summary: "학교운영위원회 지역위원 당선자 공고가 게시되었습니다.",
            sourceUrl: "https://school.gyo6.net/gimcheonms/na/ntt/selectNttList.do?bbsId=68815&mi=126238"
        },
        {
            date: "2026-03-20",
            dateLabel: "2026.03.20",
            title: "제14기 학부모위원·교원위원 무투표 안내 및 당선자 공고",
            summary: "학교운영위원회 학부모위원과 교원위원 관련 공고가 게시되었습니다.",
            sourceUrl: "https://school.gyo6.net/gimcheonms/na/ntt/selectNttList.do?bbsId=68815&mi=126238"
        }
    ],
    familyLetters: [
        {
            date: "2026-04-13",
            dateLabel: "2026.04.13",
            title: "봄철 유행 감염병 예방 안내",
            summary: "수두·유행성이하선염 예방 안내가 가정통신문으로 공개되었습니다.",
            sourceUrl: "https://school.gyo6.net/gimcheonms/na/ntt/selectNttList.do?bbsId=68816&mi=126239"
        },
        {
            date: "2026-03-27",
            dateLabel: "2026.03.27",
            title: "상록수장학재단 감사와 섬김 365 안내",
            summary: "장학재단 관련 안내가 가정통신문으로 공개되었습니다.",
            sourceUrl: "https://school.gyo6.net/gimcheonms/na/ntt/selectNttList.do?bbsId=68816&mi=126239"
        },
        {
            date: "2026-03-20",
            dateLabel: "2026.03.20",
            title: "도전 꿈 성취 교육감 인증제 운영 안내",
            summary: "학생과 학부모 대상 교육감 인증제 운영 안내가 공개되었습니다.",
            sourceUrl: "https://school.gyo6.net/gimcheonms/na/ntt/selectNttList.do?bbsId=68816&mi=126239"
        }
    ],
    healthRoomItems: ["2026학년도 4월 보건소식지", "2026학년도 3월 보건소식지", "2026년 1월·2월 보건소식지"],
    mealInfo: {
        basis: "학교 홈페이지 월별 중식 식단표 2026년 3월·4월 공개 기준",
        allergyNumbers: [
            "1 난류",
            "2 우유",
            "3 메밀",
            "4 땅콩",
            "5 대두",
            "6 밀",
            "7 고등어",
            "8 게",
            "9 새우",
            "10 돼지고기",
            "11 복숭아",
            "12 토마토",
            "13 아황산류",
            "14 호두",
            "15 닭고기",
            "16 쇠고기",
            "17 오징어",
            "18 조개류",
            "19 잣"
        ],
        menus: [
            {
                date: "2026-03-03",
                kcal: "901.8 kcal",
                menu: ["대보름오곡밥(자율) (5)", "칼슘찹쌀밥", "한우소고기탕국 (5.6.16)", "돼지고기갈비찜 (5.6.10)", "대보름나물&도시락김 (5.6)", "양념떡꼬치 (2.5.6.12.13.16.18)", "동치미 (9)", "썸모어쿠키 (2.5.6.10.16)"]
            },
            {
                date: "2026-03-04",
                kcal: "900.4 kcal",
                menu: ["찰수수밥", "돼지국밥(대) (2.5.6.9.10.16)", "햄감자채볶음 (5.6.10.12.16)", "분모자마라찜닭 (1.2.4.5.6.10.15.16.18)", "석박지(완) (9)", "야채스틱&쌈장 (5.6)", "딸기"]
            },
            {
                date: "2026-03-05",
                kcal: "894.5 kcal",
                menu: ["버섯카로틴쌀밥", "근대된장국 (5.6.9)", "동인동찜갈비 (10)", "애느타리버섯들깨볶음 (5)", "체다치즈치킨롤 (2.5.6.15)", "배추김치 (9)", "그릭요플레(딸기,복숭아 택1) (2.5.11.16)"]
            },
            {
                date: "2026-03-06",
                kcal: "900.1 kcal",
                menu: ["찰현미밥", "들깨무채국 (5.6.9)", "삼색나물&고추장 (5.6)", "쭈삼불고기 (5.6.10)", "포니치킨너겟&허니머스타드 (1.2.5.6.13.15)", "배추김치 (9)", "천혜향"]
            },
            {
                date: "2026-03-09",
                kcal: "891.1 kcal",
                menu: ["찰흑미밥", "딸기생크림와플 (1.2.5.6)", "치즈부대찌개 (1.2.5.6.9.10.12.16)", "청포묵김가루무침 (5.6)", "빠닭강정 (2.4.5.6.15.16)", "명란떡갈비구이 (5.6.10.15.18)", "배추김치 (9)"]
            },
            {
                date: "2026-03-10",
                kcal: "906.1 kcal",
                menu: ["참치생채소비빔밥 (5.6.13)", "우동장국 (1.2.5.6.9.16.18)", "꿀치즈방울토마토 (2.12)", "왕새우튀김 (1.5.6.9.13)", "배추김치 (9)", "치즈크러스트피자 (2.5.6.12.16)"]
            },
            {
                date: "2026-03-11",
                kcal: "894.0 kcal",
                menu: ["찰현미밥", "차돌순두부짜글이 (5.6.9.16.17.18)", "해물잡채 (1.5.6.9.17.18)", "양념고갈비 (5.6.7.13)", "배추김치 (9)", "셀프치킨또띠아 (1.2.5.6.12.15.16.18)"]
            },
            {
                date: "2026-03-12",
                kcal: "909.1 kcal",
                menu: ["칼슘찹쌀밥", "얼큰수육국밥(대) (5.6.9.10.13.16)", "닭봉찜닭 (2.5.6.13.15.18)", "도토리묵&양념장 (5.6)", "부추양파무침 (13)", "콘오징어핫바 (1.5.6.12.17)", "깍두기 (9)"]
            },
            {
                date: "2026-03-13",
                kcal: "899.9 kcal",
                menu: ["녹차카테킨쌀밥", "해물탕 (5.8.9.17)", "새송이버섯파프리카볶음 (5.6.18)", "파채돼지불고기 (2.5.6.10.13.15)", "햄구이 (10)", "배추김치 (9)", "한라봉"]
            },
            {
                date: "2026-03-16",
                kcal: "907.8 kcal",
                menu: ["눈꽃치즈목살필라프 (2.5.6.10.16.18)", "알리올리오파스타 (1.2.5.6.9.10.13.18)", "옥수수크림스프 (2.5.6.13.16)", "배추김치(완) (9)", "토마토치킨스테이크 (1.2.5.6.8.9.10.12.13.15.16.17.18.19)", "레몬워터젤리", "양상추샐러드&블루베리드레싱 (1.2.5)"]
            },
            {
                date: "2026-03-17",
                kcal: "907.3 kcal",
                menu: ["찰현미밥", "쇠고기미역국 (5.6.9.16)", "콩나물파채무침 (5.6.13)", "달걀스팸구이&케첩 (1.5.10.12)", "배추김치(완) (9)", "무뼈칼집갈비맛구이 (2.5.6.10.16)", "딸기"]
            },
            {
                date: "2026-03-18",
                kcal: "895.1 kcal",
                menu: ["돈까스마요덮밥 (1.5.6.10.12.16.18)", "국물떡볶이 (1.5.6.13)", "보코치니샐러드 (2.5.6.12.13)", "단무지", "배추김치(완) (9)", "모듬튀김(김말이,고구마,오징어) (5.6.17)", "피크닉 (2)"]
            },
            {
                date: "2026-03-19",
                kcal: "905.5 kcal",
                menu: ["쌀밥", "한우소머리국밥(대) (2.5.6.16)", "사각어묵간장조림 (1.5.6.13)", "아삭고추된장무침 (5.6.13)", "낙삼불고기 (5.6.10.13)", "석박지(완) (9)", "귤"]
            },
            {
                date: "2026-03-20",
                kcal: "895.7 kcal",
                menu: ["기장밥&도시락김", "김치만두국 (1.2.5.6.9.10.16)", "소보로감자조림 (5.6.13.16.18)", "명이나물 (5.6)", "고추마요닭강정 (5.6.15)", "무말랭이김치", "짜먹는요플레 (2)"]
            },
            {
                date: "2026-03-23",
                kcal: "890.2 kcal",
                menu: ["차조밥", "해물짬뽕국 (5.6.8.9.10.17.18)", "난자완스 (1.2.5.6.10.13.16.18)", "청포묵야채무침 (5.6.13)", "배추김치(완) (9)", "파채유린기&소스 (5.6.15)", "쿠키앤크림아이스크림 (2.5.6)"]
            },
            {
                date: "2026-03-24",
                kcal: "907.9 kcal",
                menu: ["봉춘찜닭 (5.15)", "찰보리밥", "진도대파육개장 (2.5.6.10.15.16.18)", "꽈리메추리알장조림 (1.5.6.13)", "근대나물무침 (5.6)", "배추김치(완) (9)", "파인애플"]
            },
            {
                date: "2026-03-25",
                kcal: "911.1 kcal",
                menu: ["찰흑미밥", "골뱅이비빔면 (5.6.13)", "된장찌개 (5.6.9)", "보쌈김치 (9)", "족발&보쌈 (1.2.10.16)", "매실주스", "상추,깻잎&쌈장 (4.5.6)"]
            },
            {
                date: "2026-03-26",
                kcal: "893.2 kcal",
                menu: ["제육덮밥 (5.6.10.13)", "맑은콩나물국 (5.6.9)", "햄야채달걀찜 (1.2.5.6.10.15.16)", "시금치무침", "새우볼&어니언소스 (1.2.5.6.9)", "깍두기(완) (9)", "골드키위"]
            },
            {
                date: "2026-03-27",
                kcal: "897.6 kcal",
                menu: ["찰현미밥", "한돈대패마라탕 (2.4.5.6.9.10.12.13.15.16.17.18)", "동파육 (5.6.10.16.18)", "백오이무침 (13)", "바게트멘보샤 (1.2.5.6.9.13)", "배추김치 (9)", "망고바나나라씨 (2)"]
            },
            {
                date: "2026-03-30",
                kcal: "896.7 kcal",
                menu: ["찰흑미밥", "유부김치국 (5.6.9)", "오이부추무침 (13)", "미소된장오리불고기 (5.6.13.16)", "바삭후라이드고등어 (3.5.6.7)", "깍두기(완) (9)", "우유마카롱 (1.2.6)"]
            },
            {
                date: "2026-03-31",
                kcal: "904.7 kcal",
                menu: ["봄나물비빔밥&약고추장 (1.5.6.10.13)", "쇠고기탕국 (5.16)", "춘천닭갈비 (5.6.13.15)", "감자전&간장 (2.5.6.12.13.16)", "파김치 (9)", "방울토마토 (12)"]
            },
            {
                date: "2026-04-01",
                kcal: "902.1 kcal",
                menu: ["차수수밥", "꽃게탕 (5.6.8.9)", "야채달걀찜 (1)", "배추김치(완) (9)", "납작씬통카츠&소스 (2.5.6.10.12.13.16.18)", "깍두기아이스크림 (2)", "양상추샐러드&딸기드레싱 (1.2.5)"]
            },
            {
                date: "2026-04-02",
                kcal: "892.1 kcal",
                menu: ["찰현미밥", "투움바파스타 (1.2.5.6.9.10.12.13.15)", "양송이크림스프 (2.5.6.12.13.16.18)", "시리얼샐러드 (1.2.5.6.12)", "배추김치(완) (9)", "목살스테이크&콘립,소스 (2.5.6.10.12.16.18)", "그린키위"]
            },
            {
                date: "2026-04-03",
                kcal: "889.6 kcal",
                menu: ["찰보리밥", "바움쿠헨(식목일) (1.2.4.5.6)", "들깨미역국 (5.6.9)", "취나물무침 (5.6)", "낙삼새볶음 (5.9.10)", "배추김치(완) (9)", "언양식바싹불고기&파무침 (2.5.6.10.13.15.16.18)"]
            },
            {
                date: "2026-04-06",
                kcal: "899.1 kcal",
                menu: ["강황쌀밥", "도토리묵국 (5.6.9)", "콩강정멸치조림 (5.6.9.13)", "달래닭불고기 (5.6.13.15)", "삼색꿀떡", "베이컨봄나물전 (1.2.5.6.10)", "깍두기(완) (9)"]
            },
            {
                date: "2026-04-07",
                kcal: "906.2 kcal",
                menu: ["흑미밥&도시락김", "차돌된장찌개 (2.5.6.16)", "돈삼겹김치찜 (5.6.9.10.13)", "콩나물쪽파무침 (5.6)", "채소달걀말이 (1)", "석박지(완) (9)", "컵과일(사과)"]
            },
            {
                date: "2026-04-08",
                kcal: "900.0 kcal",
                menu: ["찰현미밥", "김비빔면 (1.2.5.6.10.15.16)", "해물순두부찌개 (5.6.9.10.13.17.18)", "볶음김치 (5.9.13)", "캠핑모듬구이 (2.5.6.10.15.16)", "배즙", "상추,깻잎&쌈장 (4.5.6)"]
            },
            {
                date: "2026-04-09",
                kcal: "898.4 kcal",
                menu: ["꼬마김밥(참치,진미채) (1.2.5.6.13.16.17)", "추가밥&파래자반", "치즈핫도그&케찹 (1.5.6.10.12)", "종합어묵국 (1.5.6.9)", "로제떡볶이&순대튀김 (1.2.5.6.9.10.16)", "참외샐러드 (13)", "배추김치(완) (9)"]
            },
            {
                date: "2026-04-10",
                kcal: "913.6 kcal",
                menu: ["쭈꾸미채소비빔밥 (5.6.13)", "갈릭고르곤졸라피자 (1.2.5.6)", "맑은무채국 (5.6.9)", "돌나물토마토샐러드 (5.6.12.13.14)", "배추김치(완) (9)", "가자미유린기 (1.5.6.13)"]
            },
            {
                date: "2026-04-13",
                kcal: "900.5 kcal",
                menu: ["칼슘찹쌀밥", "달걀파국 (1.5.6)", "마늘화끈찜갈비 (5.6.10.13)", "상추파채무침 (5.6.13)", "배추김치(완) (9)", "싸먹는오리햄 (1.2.5.6.12.15.16)", "망고에이드"]
            },
            {
                date: "2026-04-14",
                kcal: "890.6 kcal",
                menu: ["소불고기주먹밥&추가밥,파래자반 (5.6.16)", "고기듬뿍짜장면(대) (2.5.6.9.10.13.15.16.17.18)", "단무지", "청경채겉절이 (13)", "찹쌀꿔바로우 (5.6.12.15.16)", "파김치 (9)", "파인애플(완)"]
            },
            {
                date: "2026-04-15",
                kcal: "911.6 kcal",
                menu: ["찰흑미밥", "바지락쑥된장국 (5.6.18)", "헐랭이수육&생채소무침 (5.6.10.13.15)", "열무김치비빔국수 (5.6.9.13)", "왕교자군만두&초간장 (5.6.10.13.16.18)", "보쌈김치 (9)", "초코아이스크림 (1.2)"]
            },
            {
                date: "2026-04-16",
                kcal: "917.5 kcal",
                menu: ["찰현미밥", "차돌순두부짜글이 (1.2.5.6.9.10.15.16.17.18)", "삼색나물&고추장 (5.6)", "콕찍어돈까스 (2.5.6.10.12.13.15.16.18)", "배추김치 (9)", "시금치프리타타 (1.2.10.12)", "카라향"]
            },
            {
                date: "2026-04-17",
                kcal: "900.0 kcal",
                menu: ["칼슘찹쌀밥", "한우설렁탕(대) (1.5.6.16)", "분모자순살찜닭 (2.5.6.13.15.18)", "오이고추무침 (5.6)", "벌집고구마맛탕 (5.6.13)", "깍두기 (9)", "요구얌 (2)"]
            },
            {
                date: "2026-04-20",
                kcal: "904.6 kcal",
                menu: ["오색현미쌀밥", "한우나주곰탕 (1.5.6.16)", "돈도끼떡갈비 (5.6.10.15.16.18)", "장어구이 (5.6.13.15.18)", "깍두기 (9)", "현미볼강정 (5)", "야채스틱&쌈장 (5.6)"]
            },
            {
                date: "2026-04-21",
                kcal: "908.0 kcal",
                menu: ["루테인찹쌀밥", "꼬치어묵탕 (1.2.5.6.9)", "오이배무침 (13)", "강황닭육전&소스 (1.5.6.13.15)", "당면족발 (2.5.6.10.13.15.16.18)", "배추김치 (9)", "오렌지"]
            },
            {
                date: "2026-04-22",
                kcal: "902.2 kcal",
                menu: ["잡곡밥 (5)", "얼큰쇠고기뭇국(대) (5.6.16)", "연근우엉캐슈넛조림 (5.6.7.13)", "닭다리부추찜&양념장 (5.6.7.13.15)", "도라지진미무침 (2.5.6.12.13.16.17.18)", "배추김치 (9)", "지구의날케이크 (1.2.5.6.10.13)"]
            },
            {
                date: "2026-04-23",
                kcal: "914.4 kcal",
                menu: ["찰흑미밥", "짬뽕해물탕 (5.6.8.9.17.18)", "올방개묵김가루무침 (5.6)", "볶음우동 (2.5.6.7.13.15.18)", "돈마호크&구운야채 (2.5.6.10.12.13.15.16.18)", "배추김치 (9)", "참외"]
            },
            {
                date: "2026-04-24",
                kcal: "907.7 kcal",
                menu: ["참기름간장달걀밥 (1.5.6)", "맑은콩나물국 (5.6)", "청경채사과무침 (5.9.13)", "치즈불닭&라면사리 (1.2.5.6.10.12.13.15.16.17.18)", "배추김치 (9)", "그릭요플레(딸기,복숭아 택1) (2.5.11.16)", "미니핫도그 (1.2.5.6.10.12.16)"]
            },
            {
                date: "2026-04-27",
                kcal: "916.7 kcal",
                menu: ["흑돼지국밥(대) (2.5.6.9.10.16)", "칼슘찹쌀밥", "등갈비찜 (2.5.6.10.13.15.18)", "찐김치만두 (5.6.9.10.13.15.16)", "도토리묵돌나물무침 (5.6)", "석박지(완) (9)", "딸기"]
            },
            {
                date: "2026-04-28",
                kcal: "915.0 kcal",
                menu: ["찰현미밥", "된장찌개 (5.6.9)", "동태전 (1.5.6.9)", "새콤미나리무침 (5.6.13)", "고추장삼겹살구이 (2.5.6.10.13)", "배추김치 (9)", "모듬쌈&쌈장"]
            },
            {
                date: "2026-04-29",
                kcal: "906.7 kcal",
                menu: ["추가밥&파래자반", "베트남쌀국수(대) (5.6.12.13.15.16)", "크림치즈마늘빵 (1.2.5.6)", "양파초절이 (13)", "빵가루애호박전&초간장 (1.2.5.6.13)", "치마호크 (5.6.12.15)", "배추김치 (9)"]
            },
            {
                date: "2026-04-30",
                kcal: "902.5 kcal",
                menu: ["찰현미밥", "쇠고기미역국 (5.6.16)", "매운돼지갈비찜 (2.5.6.7.10.12.13.16.18)", "부추겉절이 (5.6.9)", "어묵잡채 (5.6)", "배추김치 (9)", "아이스티라미슈컵케익 (1.2.6)"]
            }
        ]
    },
    menuStructure: [
        {
            title: "학교소개",
            items: ["송설당교육재단", "학교 설립자 소개", "학교장 인사말", "교육목표", "학교연혁", "학교상징", "교직원소개", "학교현황"]
        },
        {
            title: "학교소식",
            items: ["공지사항", "학교새소식", "가정통신문", "보건실", "주간식단표", "월별학사일정", "연간학사일정", "급식게시판"]
        },
        {
            title: "교육활동",
            items: ["학교 규칙 및 규정", "교육과정", "학교요람", "특화교육", "성적규정"]
        },
        {
            title: "학생생활",
            items: ["학생자치회", "동아리활동", "대회안내", "학습안내", "진학정보", "온라인전자투표", "반편성조회"]
        }
    ],
    chatGuidelines: [
        "학부모가 묻는 상황을 기본으로 공손하고 차분하게 답하되, 학생이 직접 묻는 경우에도 자연스럽게 답합니다.",
        "학사일정, 급식, 공지사항, 가정통신문은 학교 홈페이지 공개 기준 또는 확인일 기준이라고 말합니다.",
        "중식 식단의 괄호 숫자는 알레르기 정보 번호이므로, 식단을 안내할 때 필요하면 알레르기 번호표도 함께 확인하라고 말합니다.",
        "학생 수와 교직원 수는 2026년 3월 학교 홈페이지 기준처럼 기준을 함께 말합니다.",
        "학생 개인 정보, 학부모 개인 정보, 교직원 개인 연락처, 내부 업무, 비공개 문서, 비공개 일정은 답하지 않습니다.",
        "수학여행과 야영은 날짜만 안내하고, 장소·준비물·참가비 같은 세부 정보는 가정통신문이나 담임선생님 안내를 확인하도록 말합니다.",
        "2026년 6월 16일 학사일정 항목은 원문 확인이 필요한 자동번역 흔적이 있어 확정 정보로 말하지 않습니다."
    ]
};

export const getUpcomingGimcheonSchoolEvents = (
    fromDate = GIMCHEON_SCHOOL_DEMO.updatedAt,
    limit = 5
): GimcheonSchoolScheduleItem[] =>
    GIMCHEON_SCHOOL_DEMO.schedule
        .filter((item) => (item.endDate ?? item.date) >= fromDate)
        .sort((left, right) => left.date.localeCompare(right.date))
        .slice(0, limit);
